import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle
} from "reactstrap";
import { useAuth } from "../../contexts/AuthContext";
import LanguageSwitcher from "../LanguageSwitcher";

const AdminNavbar = ({ brandText, groupLabel, onMenuClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const displayName = user?.displayName || user?.email?.split("@")[0] || "User";
  const initials = displayName.charAt(0).toUpperCase();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = e.target.elements.q?.value?.trim();
    if (q) navigate(`/admin/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="fd-topbar" id="navbar-main">
      <button
        type="button"
        className="fd-icon-btn d-lg-none"
        onClick={onMenuClick}
        aria-label={t("shell.openMenu")}
      >
        <i className="fas fa-bars" />
      </button>

      <div className="fd-topbar__title">
        <nav aria-label={t("coreText.shell.breadcrumb")} className="fd-topbar__crumbs">
          <Link to="/admin/dashboard">{t("shell.home")}</Link>
          {groupLabel && (
            <>
              <i className="fas fa-chevron-right" />
              <span>{groupLabel}</span>
            </>
          )}
        </nav>
        <h1 className="fd-topbar__heading">{brandText}</h1>
      </div>

      <form className="fd-topbar__search d-none d-md-flex" onSubmit={handleSearch} role="search">
        <i className="fas fa-search" />
        <input
          name="q"
          type="search"
          placeholder={t("shell.searchPlaceholder")}
          aria-label={t("shell.searchPlaceholder")}
        />
        <kbd className="d-none d-xl-inline">↵</kbd>
      </form>

      <div className="fd-topbar__actions">
        <Link to="/admin/search" className="fd-icon-btn d-none d-sm-inline-flex d-md-none" aria-label={t("nav.search")}>
          <i className="fas fa-search" />
        </Link>
        <LanguageSwitcher className="fd-topbar__lang" />
        <Link
          to="/admin/settings/notifications"
          className="fd-icon-btn"
          aria-label={t("shell.notifications")}
          title={t("shell.notifications")}
        >
          <i className="far fa-bell" />
        </Link>

        <UncontrolledDropdown className="fd-topbar__user">
          <DropdownToggle tag="button" type="button" className="fd-user-toggle">
            <span className="fd-avatar">
              {user?.photoURL ? <img alt="" src={user.photoURL} /> : initials}
            </span>
            <span className="fd-user-toggle__name d-none d-xl-inline">{displayName}</span>
            <i className="fas fa-chevron-down d-none d-xl-inline" />
          </DropdownToggle>
          <DropdownMenu right className="fd-dropdown">
            <div className="fd-dropdown__header">
              <small>{t("shell.signedInAs")}</small>
              <strong>{displayName}</strong>
              {user?.email && <span>{user.email}</span>}
            </div>
            <DropdownItem to="/admin/settings/profile" tag={Link}>
              <i className="ni ni-single-02" />
              <span>{t("settings.profile")}</span>
            </DropdownItem>
            <DropdownItem to="/admin/settings" tag={Link}>
              <i className="ni ni-settings-gear-65" />
              <span>{t("settings.title")}</span>
            </DropdownItem>
            <DropdownItem to="/admin/settings/notifications" tag={Link}>
              <i className="ni ni-bell-55" />
              <span>{t("settings.notifications")}</span>
            </DropdownItem>
            <DropdownItem to="/admin/settings/security" tag={Link}>
              <i className="ni ni-lock-circle-open" />
              <span>{t("settings.security")}</span>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={handleLogout} className="text-danger">
              <i className="ni ni-user-run" />
              <span>{t("settings.signOut")}</span>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    </header>
  );
};

export default AdminNavbar;
